import { HttpErrorResponse } from '@angular/common/http';
import { computed, inject } from '@angular/core';
import {
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withState,
} from '@ngrx/signals';
import {
  AccountSessionDto,
  ApiErrorCodeEnum,
  ApiResponse,
  TenantFeatureFlagEnum,
} from '@splitsecondconnect/ultramarine-shared';
import { SessionKindEnum } from '../enums/session-kind.enum';
import { AuthService } from '../services/auth.service';
import { catchError, finalize, Observable, of, tap, throwError } from 'rxjs';
import {
  accountSessionDtoToRenterUser,
  operatorUserDtoToModel,
} from '../mappers';
import { OperatorUserModel, RenterUserModel } from '../models';

interface AuthState {
  operatorUser: OperatorUserModel | null;
  renterUser: RenterUserModel | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  sessionKind: SessionKindEnum;
}

const initialState: AuthState = {
  operatorUser: null,
  renterUser: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
  sessionKind: SessionKindEnum.ANONYMOUS,
};

/**
 * Holds who is signed in: an operator, a renter, or nobody.
 *
 * The operator and the renter are different people. Opening one session
 * clears the other.
 */
export const AuthStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withComputed((store) => ({
    isLoggedIn: computed(() => store.isAuthenticated()),
    isSsc: computed(() => {
      const email = store.operatorUser()?.email;
      return email?.toLowerCase().endsWith('@splitsecondconnect.com') === true;
    }),
  })),
  withMethods((store, auth = inject(AuthService)) => ({
    login() {
      patchState(store, { isLoading: true, error: null });
      return auth.login().pipe(
        tap((res) => {
          patchState(store, {
            operatorUser: res.data ? operatorUserDtoToModel(res.data) : null,
            renterUser: null,
            isAuthenticated: true,
            sessionKind: SessionKindEnum.OPERATOR,
          });
        }),
        catchError((error) => {
          console.error(error);
          patchState(store, { error: error.message, isLoading: false });
          return throwError(() => error);
        }),
        finalize(() => patchState(store, { isLoading: false })),
      );
    },
    /**
     * Opens a renter session and stores the proto account on it.
     *
     * Callers receive `null` when the account read fails. A response that
     * arrives after the session is no longer a renter is not stored.
     */
    loadRenterUser(): Observable<ApiResponse<AccountSessionDto> | null> {
      patchState(store, {
        operatorUser: null,
        renterUser: null,
        isAuthenticated: true,
        sessionKind: SessionKindEnum.RENTER,
      });

      return auth.getRenterSession().pipe(
        tap((response) => {
          if (store.sessionKind() !== SessionKindEnum.RENTER) {
            return;
          }

          patchState(store, {
            renterUser: response.data
              ? accountSessionDtoToRenterUser(response.data)
              : null,
          });
        }),
        catchError((error: HttpErrorResponse) => {
          if (error.error?.errorCode === ApiErrorCodeEnum.ACCOUNT_DELETED) {
            patchState(store, {
              operatorUser: null,
              renterUser: null,
              isAuthenticated: false,
              sessionKind: SessionKindEnum.ANONYMOUS,
            });
            auth.logout();
          }

          return of(null);
        }),
      );
    },
    /**
     * Clears both signed-in people and marks the session anonymous.
     */
    unauthenticate() {
      patchState(store, {
        operatorUser: null,
        renterUser: null,
        isAuthenticated: false,
        sessionKind: SessionKindEnum.ANONYMOUS,
      });
    },
    /**
     * Whether the signed-in operator's tenant includes this feature flag.
     */
    hasFeature(slug: TenantFeatureFlagEnum): boolean {
      const flags = store.operatorUser()?.featureFlags ?? [];
      return flags.includes(slug);
    },
  })),
  withHooks((_store) => ({})),
);
